import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faSyncAlt, faChevronUp, faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { getAssetTypes } from "../../../services/scm/Utils";
import "./PackageFilter.css";

const PackageFilter = ({ onSearch, onReset, isDeployment = false }) => {
    const { t } = useTranslation();

    const [packageName, setPackageName] = useState("");
    const [packageDescription, setDescription] = useState("");
    const [packageVersion, setVersion] = useState("");
    const [expirationMode, setExpirationMode] = useState(isDeployment ? "No" : "Both");
    const [assetType, setAssetType] = useState("");
    const [assetTypes, setAssetTypes] = useState([]);
    const [publishedType, setPublishedType] = useState(isDeployment ? "Yes" : "Both");

    // NEW: collapse state
    const [collapsed, setCollapsed] = useState(false);

    const loadAssetTypes = async () => {
        const raw = await getAssetTypes();
        setAssetTypes(raw?.thingTypes || []);
    };

    const buildSearchData = () => {
        const raw = {
            packageName,
            status: publishedType.toLowerCase(), // "yes" | "no" | "both"
            description: packageDescription,
            expired: expirationMode.toLowerCase(), // "yes" | "no" | "both"
            versionName: packageVersion,
            assetType
        };

        const filtered = {};
        Object.keys(raw).forEach((k) => {
            if (raw[k] !== "" && raw[k] !== null && raw[k] !== undefined) {
                filtered[k] = raw[k];
            }
        });
        return filtered;
    };

    const handleSearch = () => {
        onSearch?.(buildSearchData());
    };

    const handleReset = () => {
        setPackageName("");
        setDescription("");
        setVersion("");
        setExpirationMode(isDeployment ? "No" : "Both");
        setAssetType("");
        setPublishedType(isDeployment ? "Yes" : "Both");
        onReset?.(buildSearchData());
    };

    const loadData = async () => {
        try {
            loadAssetTypes();
        } catch (err) {
            console.error("Failed fetching asset Types", err);
        }
    };

    useEffect(() => {
        loadData(buildSearchData());
    }, []);

    useEffect(() => {
        if (!onSearch) return;
        onSearch(buildSearchData());
    }, [expirationMode, publishedType]);

    return (
        <div className="card my-2 bg-light no-border position-relative">
            <div className="card-body">
                {/* Collapsible content */}
                {!collapsed && (
                    <div className="row g-1 align-items-center">
                        {/* Package name */}
                        <div className="col-12 col-md-2">
                            <label className="col-form-label">{t("Package name")}</label>
                        </div>
                        <div className="col-12 col-md-4">
                            <input
                                type="text"
                                className="form-control"
                                value={packageName}
                                onChange={(e) => setPackageName(e.target.value)}
                                placeholder={t("Enter package name")}
                            />
                        </div>

                        {/* Package Version */}
                        <div className="col-12 col-md-2">
                            <label className="col-form-label">{t("Package Version")}</label>
                        </div>
                        <div className="col-12 col-md-4">
                            <input
                                type="text"
                                className="form-control"
                                value={packageVersion}
                                onChange={(e) => setVersion(e.target.value)}
                                placeholder={t("Enter Version")}
                            />
                        </div>

                        {/* Package Description */}
                        <div className="col-12 col-md-2">
                            <label className="col-form-label">{t("Package Description")}</label>
                        </div>
                        <div className="col-12 col-md-4">
                            <input
                                type="text"
                                className="form-control"
                                value={packageDescription}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder={t("Enter description")}
                            />
                        </div>

                        {/* Asset Type */}
                        <div className="col-12 col-md-2">
                            <label className="col-form-label">{t("Asset Type")}</label>
                        </div>
                        <div className="col-12 col-md-4">
                            <select
                                className="form-select"
                                value={assetType}
                                onChange={(e) => setAssetType(e.target.value)}
                            >
                                <option value="">{t("Select Asset Type")}</option>
                                {assetTypes.map((key) => (
                                    <option key={key} value={key}>
                                        {key}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Expiration Filter */}
                        {!isDeployment && <>
                            <div className="col-12 col-md-2">
                                <label className="col-form-label">{t("Expiration")}</label>
                            </div>
                            <div className="col-12 col-md-4">
                                <div className="btn-group w-100">
                                    {["Both", "Yes", "No"].map((exp) => (
                                        <button
                                            key={exp}
                                            type="button"
                                            className={`btn ${expirationMode === exp ? "btn-primary" : "btn-outline-secondary"}`}
                                            onClick={() => { setExpirationMode(exp); }}
                                        >
                                            {t(exp)}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </>
                        }

                        {/* Published Filter */}
                        {!isDeployment && <>
                            <div className="col-12 col-md-2">
                                <label className="col-form-label">{t("Published")}</label>
                            </div>
                            <div className="col-12 col-md-4">
                                <div className="btn-group w-100">
                                    {["Both", "Yes", "No"].map((val) => (
                                        <button
                                            key={val}
                                            type="button"
                                            className={`btn ${publishedType === val ? "btn-primary" : "btn-outline-secondary"}`}
                                            onClick={() => { setPublishedType(val); }}
                                        >
                                            {t(val)}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </>}

                        {/* Actions */}
                        <div className="col-12 mt-2 d-flex justify-content-between align-items-center">

                            {/* Collapse button (left side in same row) */}
                            <button
                                type="button"
                                className="btn btn-transperant"
                                onClick={() => setCollapsed((v) => !v)}
                                aria-label={collapsed ? t("Expand") : t("Collapse")}
                                title={collapsed ? t("Expand") : t("Collapse")}
                            >
                                <FontAwesomeIcon icon={collapsed ? faChevronDown : faChevronUp} />
                            </button>

                            {/* Search / Reset (right side) */}
                            <div className="d-flex gap-2">
                                <button className="btn btn-primary" onClick={handleSearch}>
                                    <FontAwesomeIcon icon={faSearch} className="me-1" />
                                    {t("Search")}
                                </button>
                                <button className="btn btn-secondary" onClick={handleReset}>
                                    <FontAwesomeIcon icon={faSyncAlt} className="me-1" />
                                    {t("Reset")}
                                </button>
                            </div>
                        </div>

                    </div>
                )}
                {collapsed && (

                    <div className="d-flex align-items-center ">
                        <button
                            type="button"
                            className="btn-transperant"
                            onClick={() => setCollapsed((v) => !v)}
                            aria-label={collapsed ? t("Expand") : t("Collapse")}
                            title={collapsed ? t("Expand") : t("Collapse")}
                        >
                            <FontAwesomeIcon icon={collapsed ? faChevronDown : faChevronUp} />
                        </button>
                        <label className="col-form-label">{t("Filters")}</label>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PackageFilter;
